import { useCallback, RefObject } from "react"
import { useTranslation } from "react-i18next"
import { useDragAndDrop, type UseDragAndDropResult } from "./useDragAndDrop"
import { FileUploadCollectorHandle } from "@/components"
import { Workspace } from "@/types"

interface UseFileUploadDropProps {
  fileUploadRef: RefObject<FileUploadCollectorHandle>
  workspace?: Workspace
  logPrefix?: string
  onError?: (title: string, message: string) => void
  onDragStateChange?: (isDragging: boolean) => void
}

/**
 * Custom hook that combines drag and drop with file upload handling
 * @param fileUploadRef Reference to the FileUploadCollector component
 * @param workspace Current workspace for file upload
 * @param logPrefix Prefix for console logs
 * @param onError Callback for error handling
 * @param onDragStateChange Callback when drag state changes
 * @returns Object containing isDragging state and drag handlers
 */
export const useFileUploadDrop = ({
  fileUploadRef,
  workspace,
  logPrefix = "FileUpload",
  onError,
  onDragStateChange,
}: UseFileUploadDropProps): UseDragAndDropResult => {
  const { t } = useTranslation()
  const handleFilesDropped = useCallback(
    async (droppedFiles: File[]) => {
      if (droppedFiles.length === 0) return

      if (!fileUploadRef.current) {
        console.error(`${logPrefix} - fileUploadRef is not available`)
        onError?.(
          "common.fileUpload.error.title",
          t("common.fileUpload.error.defaultMessage")
        )
        return
      }

      if (!workspace) {
        console.error(`${logPrefix} - No workspace available for file upload`)
        onError?.(
          "common.fileUpload.error.title",
          t("common.fileUpload.error.defaultMessage")
        )
        return
      }

      console.log(
        `${logPrefix} - Calling handleFiles with workspace ID:`,
        workspace.id
      )
      await fileUploadRef.current.handleFiles(droppedFiles)
    },
    [fileUploadRef, workspace, logPrefix, onError, t]
  )

  // Use the drag and drop hook internally
  return useDragAndDrop({
    onFilesDropped: handleFilesDropped,
    onDragStateChange,
    logPrefix,
  })
}

export default useFileUploadDrop
